import React, { useState } from "react";
import {
    Callout,
    DirectionalHint,
    TextField,
    PrimaryButton,
    DefaultButton,
    Icon,
} from "@fluentui/react";

export interface FeedbackCalloutProps {
    targetElement: HTMLElement | null;
    isOpen: boolean;
    onDismiss: () => void;
    onSubmit: (rating: number | null, feedbackText: string) => Promise<void>;
    isSubmitting: boolean;
    isDisabled?: boolean; // e.g., no session selected
}

const FeedbackCallout: React.FC<FeedbackCalloutProps> = ({
    targetElement,
    isOpen,
    onDismiss,
    onSubmit,
    isSubmitting,
    isDisabled = false,
}) => {
    const [rating, setRating] = useState<number | null>(null);
    const [feedbackText, setFeedbackText] = useState<string>("");

    const handleDismiss = () => {
        setRating(null);
        setFeedbackText("");
        onDismiss();
    };

    const handleSubmit = async () => {
        await onSubmit(rating, feedbackText);
        // Reset state after successful submit (parent will close the callout)
        setRating(null);
        setFeedbackText("");
    };

    const isSubmitDisabled = (!feedbackText.trim() && rating === null) || isSubmitting || isDisabled;

    if (!isOpen || !targetElement) {
        return null;
    }

    return (
        <Callout
            target={targetElement}
            onDismiss={handleDismiss}
            directionalHint={DirectionalHint.bottomAutoEdge}
            isBeakVisible={true}
            beakWidth={16}
            gapSpace={4}
            setInitialFocus
            calloutMaxWidth={400}
            styles={{
                root: {
                    padding: "0",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.14)",
                    borderRadius: "8px",
                    border: "1px solid #e1dfdd",
                    overflow: "visible",
                },
                calloutMain: {
                    padding: "0",
                    borderRadius: "8px",
                    overflow: "hidden",
                },
                beak: {
                    backgroundColor: "#fff",
                },
            }}
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "380px",
                    backgroundColor: "#fff",
                }}
            >
                {/* Title */}
                <div
                    style={{
                        padding: "16px 16px 12px 16px",
                        fontWeight: 600,
                        fontSize: "14px",
                        color: "#242424",
                    }}
                >
                    Submit feedback for this chat session
                </div>

                {/* Content */}
                <div
                    style={{
                        padding: "0 16px 16px 16px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                    }}
                >
                    {/* Feedback Text */}
                    <TextField
                        placeholder="Share your feedback about this AI Hub chat session..."
                        multiline
                        rows={3}
                        value={feedbackText}
                        onChange={(_, newValue) => setFeedbackText(newValue || "")}
                        styles={{
                            fieldGroup: {
                                borderRadius: "4px",
                                border: "1px solid #8a8886",
                            },
                        }}
                    />

                    {/* Star Rating */}
                    <StarRating rating={rating} onRatingChange={setRating} />

                    {/* Footer Buttons */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "8px",
                            marginTop: "8px",
                        }}
                    >
                        <DefaultButton
                            onClick={handleDismiss}
                            text="Cancel"
                            styles={{
                                root: {
                                    minWidth: "80px",
                                    height: "32px",
                                    borderRadius: "4px",
                                    border: "1px solid #8a8886",
                                },
                                label: { fontSize: "14px", fontWeight: 400 },
                            }}
                        />
                        <PrimaryButton
                            onClick={handleSubmit}
                            text="Submit"
                            disabled={isSubmitDisabled}
                            styles={{
                                root: {
                                    minWidth: "80px",
                                    height: "32px",
                                    borderRadius: "4px",
                                    backgroundColor: "#0078d4",
                                    border: "none",
                                },
                                rootHovered: {
                                    backgroundColor: "#106ebe",
                                },
                                rootDisabled: {
                                    backgroundColor: "#f3f2f1",
                                },
                                label: { fontSize: "14px", fontWeight: 400 },
                            }}
                        />
                    </div>
                </div>
            </div>
        </Callout>
    );
};

// Separate Star Rating component for reusability
interface StarRatingProps {
    rating: number | null;
    onRatingChange: (rating: number) => void;
    maxStars?: number;
}

const StarRating: React.FC<StarRatingProps> = ({
    rating,
    onRatingChange,
    maxStars = 5,
}) => {
    const [hoveredStar, setHoveredStar] = useState<number | null>(null);

    const displayRating = hoveredStar !== null ? hoveredStar : (rating ?? 0);

    return (
        <div>
            <div
                style={{
                    fontSize: "13px",
                    color: "#242424",
                    fontWeight: 600,
                    marginBottom: "8px",
                }}
            >
                Rate your experience
            </div>
            <div style={{ display: "flex", gap: "4px" }}>
                {Array.from({ length: maxStars }, (_, i) => i + 1).map((star) => (
                    <button
                        key={star}
                        onClick={() => onRatingChange(star)}
                        onMouseEnter={() => setHoveredStar(star)}
                        onMouseLeave={() => setHoveredStar(null)}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "transform 0.1s ease",
                            transform: hoveredStar === star ? "scale(1.1)" : "scale(1)",
                        }}
                        aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
                    >
                        <Icon
                            iconName={star <= displayRating ? "FavoriteStarFill" : "FavoriteStar"}
                            style={{
                                fontSize: "24px",
                                color: star <= displayRating ? "#0078d4" : "#8a8886",
                            }}
                        />
                    </button>
                ))}
            </div>
        </div>
    );
};

export default FeedbackCallout;
export { StarRating };
